/**
 * Block versions on log entries (ADR-0103).
 *
 * A block has a MAJOR (`version`, `v1` — pinned by compositions and
 * refs) and a MINOR (`minor` on its definition, a drop-in revision). A
 * content entry records the version of the block it was made against
 * (`blockVersion: 'v1.3'`) and the structure fingerprint of its target
 * (`structureFingerprint`). When the target's text fingerprint no longer
 * matches but the block is now a newer version and the target's structure
 * is unchanged, the replay carries a whole-text edit over instead of
 * reporting a conflict — computed from structure, not from how the new
 * version was classified.
 */
import { resolveAnchor, resolvePartAnchor } from './address.js';
import { fingerprintNodeStructure, fingerprintPartStructure } from './structure.js';
/** `v1` + 3 → `v1.3`. */
export const formatBlockVersion = (version, minor) => `${version}.${minor ?? 0}`;
/** `v1.3` → `{ major: 'v1', minor: 3 }`; `null` when malformed. */
export function parseBlockVersion(value) {
    const m = /^(.+)\.(\d+)$/.exec(value);
    if (!m)
        return null;
    return { major: m[1], minor: Number(m[2]) };
}
const majorNumber = (major) => {
    const m = /^v(\d+)$/.exec(major);
    return m ? Number(m[1]) : null;
};
/** Whether `current` is a later block version than `recorded` (higher minor, or a later major). */
export function isNewerBlockVersion(recorded, current) {
    const a = parseBlockVersion(recorded);
    const b = parseBlockVersion(current);
    if (!a || !b)
        return false;
    if (a.major === b.major)
        return b.minor > a.minor;
    const na = majorNumber(a.major);
    const nb = majorNumber(b.major);
    return na != null && nb != null && nb > na;
}
/** `instanceId → 'v1.3'` from a compiled document's manifest. */
export const instanceBlockVersions = (document) => new Map(document.manifest.map((m) => [m.instanceId, formatBlockVersion(m.blockVersion, m.blockMinor)]));
const VERSIONED_OPS = new Set(['replaceText', 'removeNode', 'replaceRow', 'removeRow', 'insertParagraph', 'insertClause', 'insertRow']);
/**
 * Stamps `blockVersion` + `structureFingerprint` on a content entry anchored
 * to a node (or part) of `compiled`. Other entries, entries anchored to an
 * earlier entry's output, and whole-instance anchors are returned unchanged.
 */
export function stampBlockVersion(compiled, entry) {
    if (!VERSIONED_OPS.has(entry.op) || !('anchor' in entry) || !entry.anchor || 'fromSeq' in entry.anchor)
        return entry;
    const anchor = entry.anchor;
    if (anchor.sectionId == null && !anchor.path?.length)
        return entry;
    const version = instanceBlockVersions(compiled).get(anchor.instanceId);
    if (!version)
        return entry;
    const node = resolveAnchor(compiled, anchor);
    let structure = null;
    if (node?.kind === 'node')
        structure = fingerprintNodeStructure(node.node);
    else {
        const part = resolvePartAnchor(compiled, anchor);
        if (part)
            structure = fingerprintPartStructure(part.owner, part.part);
    }
    if (!structure)
        return entry;
    return { ...entry, blockVersion: version, structureFingerprint: structure };
}
//# sourceMappingURL=version.js.map